import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWeb3 } from '../context/Web3Context';
import { toast } from 'sonner';
import { 
  ArrowLeft, Car, Shield, FileText, CheckCircle, AlertTriangle, 
  Calculator, Printer, DollarSign, Calendar, ChevronRight, Lock,
  Building, Users, Landmark, RefreshCw, Info, Plus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BETH_TOKEN = "0x6822824c5349bf005e353ec5dda3fa34595ea811";

// Treasury-Indexed Auto Obligation Products for NEW financing
const TIAO_PRODUCTS = [
  {
    id: 'tiao-36',
    name: 'TIAO-36 (3-Year)',
    description: '36-month Treasury-indexed auto obligation',
    term: 36,
    referenceRate: 'U.S. Treasury 2-Year CMR',
    baseAPR: 4.99,
    spread: 2.00,
    minAmount: 10000,
    maxAmount: 75000,
    features: ['BETH Credit Enhancement', 'PAS Protection', 'Fastest Payoff']
  },
  {
    id: 'tiao-48',
    name: 'TIAO-48 (4-Year)',
    description: '48-month Treasury-indexed auto obligation',
    term: 48,
    referenceRate: 'U.S. Treasury 3-Year CMR',
    baseAPR: 5.49,
    spread: 2.25,
    minAmount: 15000,
    maxAmount: 100000,
    features: ['BETH Credit Enhancement', 'PAS Protection', 'Balanced Term']
  },
  {
    id: 'tiao-60',
    name: 'TIAO-60 (5-Year)',
    description: '60-month Treasury-indexed auto obligation',
    term: 60,
    referenceRate: 'U.S. Treasury 5-Year CMR',
    baseAPR: 5.99,
    spread: 2.50,
    minAmount: 20000,
    maxAmount: 150000,
    features: ['BETH Credit Enhancement', 'PAS Protection', 'Lower Monthly']
  },
  {
    id: 'tiao-72',
    name: 'TIAO-72 (6-Year)',
    description: '72-month Treasury-indexed auto obligation',
    term: 72,
    referenceRate: 'U.S. Treasury 5-Year CMR',
    baseAPR: 6.49,
    spread: 2.75,
    minAmount: 25000,
    maxAmount: 200000,
    features: ['BETH Credit Enhancement', 'PAS Protection', 'Lowest Monthly']
  }
];

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
};

const NewAutoFinancing = () => {
  const { user, token } = useAuth();
  const { account, bethBalance } = useWeb3();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [vehicleAmount, setVehicleAmount] = useState('');
  const [costSheet, setCostSheet] = useState(null);
  const [orders, setOrders] = useState([]);
  
  // Form for creating new auto financing
  const [formData, setFormData] = useState({
    borrower_name: '',
    borrower_wallet: '',
    vehicle_vin: '',
    vehicle_description: '',
    dealer_name: '',
    dealer_contact: '',
    dealer_address: '',
    down_payment: '',
    trade_in_value: '',
    beth_collateral: ''
  });

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchOrders();
  }, [token]);

  useEffect(() => {
    if (account) {
      setFormData(prev => ({ ...prev, borrower_wallet: account }));
    }
  }, [account]);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({ ...prev, borrower_name: user.name || user.email }));
    }
  }, [user]);

  const fetchOrders = async () => {
    try {
      const res = await fetch(`${API}/auto-financing/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setOrders(data.orders?.filter(o => !o.is_refinance) || []);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    if (vehicleAmount) {
      calculateCostSheet(product, parseFloat(vehicleAmount));
    }
  };

  const handleAmountChange = (value) => {
    setVehicleAmount(value);
    if (selectedProduct && value) {
      calculateCostSheet(selectedProduct, parseFloat(value));
    }
  };

  const calculateCostSheet = (product, principal) => {
    if (!product || !principal || principal < product.minAmount) {
      setCostSheet(null);
      return;
    }
    
    const downPayment = parseFloat(formData.down_payment) || 0;
    const tradeIn = parseFloat(formData.trade_in_value) || 0;
    const netPrincipal = principal - downPayment - tradeIn;
    
    const indexedAPR = product.baseAPR;
    const monthlyRate = indexedAPR / 100 / 12;
    const numPayments = product.term;
    
    const monthlyPayment = netPrincipal * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / (Math.pow(1 + monthlyRate, numPayments) - 1);
    const totalPayments = monthlyPayment * numPayments;
    const totalIndexAdjustment = totalPayments - netPrincipal;
    
    // Platform fees
    const structuringFee = netPrincipal * 0.02;
    const pasEnhancementFee = netPrincipal * 0.01;
    const adminFee = 250;
    const settlementFee = 150;
    const totalFees = structuringFee + pasEnhancementFee + adminFee + settlementFee;
    
    // BETH collateral requirement (10% of principal at $1.00/BETH)
    const bethRequired = Math.ceil(netPrincipal * 0.10);
    
    setCostSheet({
      principal: netPrincipal.toFixed(2),
      grossAmount: principal.toFixed(2),
      downPayment: downPayment.toFixed(2),
      tradeIn: tradeIn.toFixed(2),
      indexedAPR: indexedAPR.toFixed(2),
      term: product.term,
      monthlyPayment: monthlyPayment.toFixed(2),
      totalPayments: totalPayments.toFixed(2),
      totalIndexAdjustment: totalIndexAdjustment.toFixed(2),
      structuringFee: structuringFee.toFixed(2),
      pasEnhancementFee: pasEnhancementFee.toFixed(2),
      adminFee: adminFee.toFixed(2),
      settlementFee: settlementFee.toFixed(2),
      totalFees: totalFees.toFixed(2),
      bethCollateral: bethRequired,
      collateralRatio: 10
    });
    
    setFormData(prev => ({ ...prev, beth_collateral: bethRequired.toString() }));
  };

  const handleCreateOrder = async () => {
    if (!selectedProduct || !costSheet) {
      toast.error('Please select a product and enter vehicle amount');
      return;
    }
    
    if (!formData.vehicle_vin || formData.vehicle_vin.length < 17) {
      toast.error('Please enter a valid 17-character VIN');
      return;
    }
    
    if (!formData.dealer_name) {
      toast.error('Please enter dealer/seller name');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/auto-financing/order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          product_id: selectedProduct.id,
          product_name: selectedProduct.name,
          principal_amount: parseFloat(costSheet.principal),
          gross_amount: parseFloat(costSheet.grossAmount),
          down_payment: parseFloat(costSheet.downPayment),
          trade_in_value: parseFloat(costSheet.tradeIn),
          indexed_apr: parseFloat(costSheet.indexedAPR),
          term_months: selectedProduct.term,
          monthly_payment: parseFloat(costSheet.monthlyPayment),
          treasury_index: selectedProduct.referenceRate,
          beth_collateral: parseFloat(costSheet.bethCollateral),
          is_refinance: false
        })
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success('Auto Finance Order created successfully!');
        fetchOrders();
        setVehicleAmount('');
        setCostSheet(null);
        setFormData(prev => ({
          ...prev,
          vehicle_vin: '',
          vehicle_description: '',
          dealer_name: '',
          dealer_contact: '',
          dealer_address: '',
          down_payment: '',
          trade_in_value: ''
        }));
      } else {
        toast.error(data.error || 'Failed to create order');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error creating finance order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="new-auto-financing-page">
      {/* Header */}
      <header className="bg-[#0d1d35] border-b border-[#1a2d4a]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="text-gray-400 hover:text-white">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-white flex items-center gap-2">
                  <Car className="w-6 h-6 text-cyan-400" />
                  New Auto Financing
                </h1>
                <p className="text-sm text-gray-400">Treasury-Indexed Auto Obligation (TIAO) - New Vehicle Purchase</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/auto-refinance">
                <Button variant="outline" className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refinance Existing Loan
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Disclaimer Banner */}
      <div className="bg-gradient-to-r from-yellow-900/30 to-yellow-800/30 border-b border-yellow-700/50">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center gap-3 text-yellow-200 text-xs">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <p>
              <strong>IMPORTANT:</strong> This is a PRIVATE CREDIT structure for NEW vehicle purchases, NOT a consumer auto loan. 
              No banking license required. Outside CFPB/TILA jurisdiction.
            </p>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Product Selection & Vehicle Details */}
          <div className="space-y-6">
            {/* Product Selection */}
            <Card className="bg-[#0d1d35] border-[#1a2d4a]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-cyan-400" />
                  Select Term Length
                </CardTitle>
                <CardDescription>Choose your financing term</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {TIAO_PRODUCTS.map((product) => (
                    <div
                      key={product.id}
                      onClick={() => handleProductSelect(product)}
                      className={`p-4 rounded-lg border cursor-pointer transition-all ${
                        selectedProduct?.id === product.id 
                          ? 'border-cyan-500 bg-cyan-500/10' 
                          : 'border-[#1a2d4a] hover:border-cyan-500/50'
                      }`}
                      data-testid={`term-${product.term}`}
                    >
                      <div className="text-2xl font-bold text-white">{product.term}mo</div>
                      <div className="text-sm text-cyan-400">{product.baseAPR}% APR</div>
                      <div className="text-xs text-gray-500 mt-1">{product.referenceRate.replace('U.S. Treasury ', '').replace(' CMR', '')}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Vehicle Details Form */}
            <Card className="bg-[#0d1d35] border-[#1a2d4a]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Car className="w-5 h-5 text-cyan-400" />
                  Vehicle & Purchase Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wider">Vehicle Price (USD)</label>
                  <Input
                    type="number"
                    value={vehicleAmount}
                    onChange={(e) => handleAmountChange(e.target.value)}
                    placeholder="Enter vehicle price"
                    className="mt-2 bg-[#0a1628] border-[#1a2d4a] text-white"
                    data-testid="vehicle-price-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-400 uppercase tracking-wider">Down Payment</label>
                    <Input
                      type="number"
                      value={formData.down_payment}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, down_payment: e.target.value }));
                        if (selectedProduct && vehicleAmount) {
                          setTimeout(() => calculateCostSheet(selectedProduct, parseFloat(vehicleAmount)), 0);
                        }
                      }}
                      placeholder="$0"
                      className="mt-2 bg-[#0a1628] border-[#1a2d4a] text-white"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 uppercase tracking-wider">Trade-In Value</label>
                    <Input
                      type="number"
                      value={formData.trade_in_value}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, trade_in_value: e.target.value }));
                        if (selectedProduct && vehicleAmount) {
                          setTimeout(() => calculateCostSheet(selectedProduct, parseFloat(vehicleAmount)), 0);
                        }
                      }}
                      placeholder="$0"
                      className="mt-2 bg-[#0a1628] border-[#1a2d4a] text-white"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wider">Vehicle VIN</label>
                  <Input
                    value={formData.vehicle_vin}
                    onChange={(e) => setFormData(prev => ({ ...prev, vehicle_vin: e.target.value.toUpperCase() }))}
                    placeholder="Enter 17-character VIN"
                    className="mt-2 bg-[#0a1628] border-[#1a2d4a] text-white font-mono"
                    maxLength={17}
                    data-testid="vehicle-vin-input"
                  />
                </div>
                
                <div>
                  <label className="text-xs text-gray-400 uppercase tracking-wider">Vehicle Description</label>
                  <Input
                    value={formData.vehicle_description}
                    onChange={(e) => setFormData(prev => ({ ...prev, vehicle_description: e.target.value }))}
                    placeholder="Year, Make, Model, Trim"
                    className="mt-2 bg-[#0a1628] border-[#1a2d4a] text-white"
                  />
                </div>
                
                <div className="border-t border-[#1a2d4a] pt-4 mt-4">
                  <h4 className="text-sm text-gray-400 mb-3">Dealer / Seller Information</h4>
                  <div className="space-y-3">
                    <Input
                      value={formData.dealer_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, dealer_name: e.target.value }))}
                      placeholder="Dealership or Seller Name"
                      className="bg-[#0a1628] border-[#1a2d4a] text-white"
                      data-testid="dealer-name-input"
                    />
                    <Input
                      value={formData.dealer_contact}
                      onChange={(e) => setFormData(prev => ({ ...prev, dealer_contact: e.target.value }))}
                      placeholder="Contact Phone or Email"
                      className="bg-[#0a1628] border-[#1a2d4a] text-white"
                    />
                    <Input
                      value={formData.dealer_address}
                      onChange={(e) => setFormData(prev => ({ ...prev, dealer_address: e.target.value }))}
                      placeholder="Dealer Address"
                      className="bg-[#0a1628] border-[#1a2d4a] text-white"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Cost Sheet */}
          <div className="space-y-6">
            <Card className="bg-[#0d1d35] border-[#1a2d4a]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-cyan-400" />
                  Finance Cost Sheet
                </CardTitle>
              </CardHeader>
              <CardContent>
                {costSheet ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-gradient-to-r from-[#0a1628] to-[#1a2d4a] rounded-lg border border-cyan-500/30">
                      <div className="text-xs text-cyan-400 uppercase tracking-wider mb-2">Monthly Payment</div>
                      <div className="text-3xl font-bold text-white">${parseFloat(costSheet.monthlyPayment).toLocaleString()}</div>
                      <div className="text-sm text-gray-400 mt-1">for {costSheet.term} months at {costSheet.indexedAPR}% APR</div>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between py-2 border-b border-[#1a2d4a]">
                        <span className="text-gray-400">Vehicle Price</span>
                        <span className="text-white">${parseFloat(costSheet.grossAmount).toLocaleString()}</span>
                      </div>
                      {parseFloat(costSheet.downPayment) > 0 && (
                        <div className="flex justify-between py-2 border-b border-[#1a2d4a]">
                          <span className="text-gray-400">Down Payment</span>
                          <span className="text-green-400">-${parseFloat(costSheet.downPayment).toLocaleString()}</span>
                        </div>
                      )}
                      {parseFloat(costSheet.tradeIn) > 0 && (
                        <div className="flex justify-between py-2 border-b border-[#1a2d4a]">
                          <span className="text-gray-400">Trade-In Value</span>
                          <span className="text-green-400">-${parseFloat(costSheet.tradeIn).toLocaleString()}</span>
                        </div>
                      )}
                      <div className="flex justify-between py-2 border-b border-[#1a2d4a]">
                        <span className="text-gray-400">Net Amount Financed</span>
                        <span className="text-white font-bold">${parseFloat(costSheet.principal).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-[#1a2d4a]">
                        <span className="text-gray-400">Total of Payments</span>
                        <span className="text-white">${parseFloat(costSheet.totalPayments).toLocaleString()}</span>
                      </div>
                    </div>
                    
                    <div className="p-3 bg-blue-900/20 border border-blue-700/50 rounded-lg">
                      <div className="text-xs text-blue-400 mb-2 font-medium">PLATFORM FEES</div>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-400">Structuring Fee (2%)</span>
                          <span className="text-white">${parseFloat(costSheet.structuringFee).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">PAS Enhancement Fee (1%)</span>
                          <span className="text-white">${parseFloat(costSheet.pasEnhancementFee).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-blue-700/50">
                          <span className="text-gray-400">Total Fees</span>
                          <span className="text-cyan-400 font-bold">${parseFloat(costSheet.totalFees).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-3 bg-[#0a1628] rounded-lg">
                      <div className="flex items-center gap-2 text-xs text-cyan-400 mb-2">
                        <Lock className="w-4 h-4" />
                        BETH CREDIT ENHANCEMENT
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">BETH Required</span>
                        <span className="text-white">{parseFloat(costSheet.bethCollateral).toLocaleString()} BETH</span>
                      </div>
                      <div className="flex justify-between text-sm mt-1">
                        <span className="text-gray-400">Collateralization</span>
                        <span className="text-white">{costSheet.collateralRatio}%</span>
                      </div>
                    </div>
                    
                    <Button 
                      className="w-full bg-cyan-600 hover:bg-cyan-700"
                      onClick={handleCreateOrder}
                      disabled={loading || !formData.vehicle_vin || !formData.dealer_name}
                      data-testid="create-finance-order-btn"
                    >
                      {loading ? 'Creating...' : 'Create Finance Order'}
                      <Plus className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Calculator className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Select a term and enter vehicle price to see cost breakdown</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* My Orders */}
            {orders.length > 0 && (
              <Card className="bg-[#0d1d35] border-[#1a2d4a]">
                <CardHeader>
                  <CardTitle className="text-white text-sm">My Finance Orders ({orders.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {orders.slice(0, 3).map((order) => (
                      <div key={order.order_id} className="p-3 bg-[#0a1628] rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="text-white text-sm font-medium">{order.vehicle_description || 'Vehicle'}</div>
                            <div className="text-xs text-gray-500">{order.order_id}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-cyan-400 font-medium">${parseFloat(order.principal_amount || 0).toLocaleString()}</div>
                            <div className={`text-xs px-2 py-0.5 rounded ${order.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                              {order.status?.toUpperCase()}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default NewAutoFinancing;
